// Google Analytics tracking utility for AI Ebook Planning Tool
// Provides comprehensive event tracking for conversion optimization and user behavior analysis

// Safe gtag function with error checking
const safeGtag = (...args) => {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag(...args);
    }
  } catch (error) {
    // Silently handle errors in production
  }
};

// E-commerce & Revenue Tracking
export const trackPurchase = (
  _transactionId,
  value = 19.0,
  currency = "USD"
) => {
  safeGtag("event", "purchase", {
    transaction_id: `pro_purchase_${Date.now()}`,
    value: value,
    currency: currency,
    items: [
      {
        item_id: "pro_monthly_subscription",
        item_name: "Pro Monthly Subscription",
        price: value,
        quantity: 1,
      },
    ],
  });
};

export const trackBeginCheckout = () => {
  safeGtag("event", "begin_checkout", {
    currency: "USD",
    value: 19.0,
    items: [
      {
        item_id: "pro_monthly_subscription",
        item_name: "Pro Monthly Subscription",
        price: 19.0,
        quantity: 1,
      },
    ],
  });
};

export const trackAddToCart = () => {
  safeGtag("event", "add_to_cart", {
    currency: "USD",
    value: 19.0,
    items: [
      {
        item_id: "pro_monthly_subscription",
        item_name: "Pro Monthly Subscription",
        price: 19.0,
        quantity: 1,
      },
    ],
  });
};

// Core Feature Usage Tracking
export const trackEbookGeneration = (userType, topicCategory, chapters) => {
  safeGtag("event", "ebook_generation", {
    user_type: userType, // 'free' or 'pro'
    topic_category: topicCategory,
    chapters_count: chapters,
    generation_timestamp: new Date().toISOString(),
  });
};

export const trackFormStart = (step = 0) => {
  safeGtag("event", "form_start", {
    step_number: step,
    step_name: getStepName(step),
    timestamp: new Date().toISOString(),
  });
};

export const trackFormComplete = (formData) => {
  safeGtag("event", "form_complete", {
    genre: formData.genre,
    chapters: formData.chapters,
    tone: formData.tone,
    has_title: !!formData.title,
    has_description: !!formData.description,
    topic_length: formData.topic ? formData.topic.length : 0,
    completion_time: new Date().toISOString(),
  });
};

export const trackFormAbandon = (step, formData) => {
  safeGtag("event", "form_abandon", {
    abandoned_at_step: step,
    step_name: getStepName(step),
    completed_fields: Object.keys(formData).filter((key) => formData[key])
      .length,
    total_fields: Object.keys(formData).length,
    abandon_timestamp: new Date().toISOString(),
  });
};

// User Engagement & Navigation Tracking
export const trackButtonClick = (buttonName, buttonContext = "") => {
  safeGtag("event", "button_click", {
    button_name: buttonName,
    button_context: buttonContext,
    timestamp: new Date().toISOString(),
  });
};

export const trackPageView = (pageName, pageCategory = "") => {
  safeGtag("event", "page_view", {
    page_name: pageName,
    page_category: pageCategory,
    timestamp: new Date().toISOString(),
  });
};

export const trackScroll = (scrollPercentage) => {
  safeGtag("event", "scroll", {
    scroll_percentage: scrollPercentage,
    timestamp: new Date().toISOString(),
  });
};

// Conversion Funnel Tracking
export const trackUpgradeInterest = (trigger = "") => {
  safeGtag("event", "upgrade_interest", {
    trigger: trigger,
    user_type: "free",
    timestamp: new Date().toISOString(),
  });
};

export const trackDemoInteraction = (interactionType, contentType = "") => {
  safeGtag("event", "demo_interaction", {
    interaction_type: interactionType,
    content_type: contentType,
    timestamp: new Date().toISOString(),
  });
};

// Utility function to get step names
const getStepName = (step) => {
  const stepNames = {
    0: "topic_selection",
    1: "audience_definition",
    2: "book_structure",
    3: "ai_prompts",
    4: "final_plan",
  };
  return stepNames[step] || `step_${step}`;
};

// Scroll tracking with throttling
let scrollTrackingTimeout;
export const trackScrollWithThrottle = (scrollPercentage) => {
  if (scrollTrackingTimeout) return;

  scrollTrackingTimeout = setTimeout(() => {
    trackScroll(scrollPercentage);
    scrollTrackingTimeout = null;
  }, 1000); // Throttle to once per second
};

// Form interaction tracking
export const trackFormInteraction = (
  fieldName,
  interactionType,
  fieldValue = ""
) => {
  safeGtag("event", "form_interaction", {
    field_name: fieldName,
    interaction_type: interactionType, // 'focus', 'blur', 'change', 'input'
    field_value_length: fieldValue ? fieldValue.length : 0,
    timestamp: new Date().toISOString(),
  });
};

// Error tracking
export const trackError = (errorType, errorMessage, errorContext = "") => {
  safeGtag("event", "error", {
    error_type: errorType,
    error_message: errorMessage,
    error_context: errorContext,
    timestamp: new Date().toISOString(),
  });
};

// Performance tracking
export const trackPerformance = (metricName, metricValue, metricUnit = "") => {
  safeGtag("event", "performance", {
    metric_name: metricName,
    metric_value: metricValue,
    metric_unit: metricUnit,
    timestamp: new Date().toISOString(),
  });
};

// User session tracking
export const trackSessionStart = () => {
  safeGtag("event", "session_start", {
    timestamp: new Date().toISOString(),
    user_agent:
      typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
    screen_resolution:
      typeof screen !== "undefined"
        ? `${screen.width}x${screen.height}`
        : "unknown",
    viewport_size:
      typeof window !== "undefined"
        ? `${window.innerWidth}x${window.innerHeight}`
        : "unknown",
  });
};

export const trackSessionEnd = (sessionDuration) => {
  safeGtag("event", "session_end", {
    session_duration_seconds: sessionDuration,
    timestamp: new Date().toISOString(),
  });
};

// Export all tracking functions
export default {
  // E-commerce
  trackPurchase,
  trackBeginCheckout,
  trackAddToCart,

  // Core features
  trackEbookGeneration,
  trackFormStart,
  trackFormComplete,
  trackFormAbandon,

  // Engagement
  trackButtonClick,
  trackPageView,
  trackScroll,
  trackScrollWithThrottle,

  // Conversion
  trackUpgradeInterest,
  trackDemoInteraction,

  // Utilities
  trackFormInteraction,
  trackError,
  trackPerformance,
  trackSessionStart,
  trackSessionEnd,
};
